/**
 * Holdings List Hook
 *
 * Fetches all holdings with fund data using the Hono RPC client
 * with TanStack Query for caching and state management.
 */

import { useQuery, queryOptions } from "@tanstack/react-query";
import { api, unwrapResponse, ApiError } from "@/lib/api";
import type { Holding, FundRating } from "@curie/shared";

// ============================================================================
// Types
// ============================================================================

/**
 * Fund data included with each holding
 */
export interface HoldingFundData {
  name: string;
  amcName: string;
  category: string;
  rating: FundRating;
  rank: number;
  totalFundsInCategory: number;
  nav: number;
  navDate: string;
  returns1Y: number;
}

/**
 * Holding with enriched fund data
 */
export interface EnrichedHolding extends Holding {
  fund: HoldingFundData | null;
}

/**
 * Holdings list response
 */
export interface HoldingsListData {
  holdings: EnrichedHolding[];
  totalCount: number;
}

// ============================================================================
// Query Options
// ============================================================================

/**
 * Query options for holdings list
 * Can be used with useQuery or prefetching
 */
export const holdingsListQueryOptions = queryOptions({
  queryKey: ["holdings", "list"] as const,
  queryFn: async (): Promise<HoldingsListData> => {
    const response = await api.v1.holdings.$get();
    return unwrapResponse<HoldingsListData>(response);
  },
  staleTime: 1000 * 60 * 5, // 5 minutes
  gcTime: 1000 * 60 * 30, // 30 minutes
});

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching all holdings with fund data
 *
 * @example
 * ```tsx
 * function HoldingsList() {
 *   const { data, isLoading, error, refetch } = useHoldings();
 *
 *   if (isLoading) return <HoldingsListSkeleton />;
 *   if (error) return <ErrorDisplay error={error} onRetry={refetch} />;
 *
 *   return (
 *     <div>
 *       {data.holdings.map((holding) => (
 *         <HoldingCard key={holding.id} data={holding} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useHoldings() {
  return useQuery(holdingsListQueryOptions);
}

// ============================================================================
// Error Handling Utilities
// ============================================================================

/**
 * Type guard for ApiError
 */
export function isHoldingsApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Get user-friendly error message for holdings errors
 */
export function getHoldingsErrorMessage(error: unknown): string {
  if (isHoldingsApiError(error)) {
    if (error.status >= 500) {
      return "Server error. Please try again later.";
    }
    return error.message;
  }

  if (error instanceof Error) {
    if (error.message.includes("fetch")) {
      return "Network error. Please check your connection.";
    }
    return error.message;
  }

  return "An unexpected error occurred while loading holdings.";
}
